import { Button, notification } from '@pankod/refine-antd'
import clsx from 'clsx'
import { Canvas, FabricObject } from 'fabric'
import { Grab, Hand, ZoomIn, ZoomOut } from 'lucide-react'
import React from 'react'

type CanvasFooterProps = {
  targetWrapperId: string
  // TODO: better debounce this
  initialScale?: number
  initialPan?: { x: number; y: number }
  onPan?: () => void
  canvas?: Canvas | null
  className?: string
  defaultPanEnabled?: boolean
}

export const CanvasFooter = (props: CanvasFooterProps) => {
  const {
    canvas,
    targetWrapperId,
    onPan,
    initialScale,
    initialPan,
    className,
    defaultPanEnabled,
  } = props
  const MSG_INFO_KEY = 'info_certificate_preview_grab_mode'
  const INITIAL_SCALE = initialScale ?? 0.7
  const SCALE_STEP = 0.1
  const [MIN_SCALE, MAX_SCALE] = [0.2, 1.5]
  const [defaultZoom, setDefaultZoom] = React.useState(INITIAL_SCALE)
  const [togglePan, setTogglePan] = React.useState(defaultPanEnabled || false)
  const [isGrabbing, setGrabbing] = React.useState(false)

  React.useEffect(() => {
    setDefaultZoom(INITIAL_SCALE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [INITIAL_SCALE])

  // init fit
  React.useEffect(() => {
    const canvasWrapper = document.getElementById(targetWrapperId)

    if (canvasWrapper) {
      if (!!initialPan) {
        canvasWrapper.style.transform = `scale(${defaultZoom}) translate(${initialPan?.x}px, ${initialPan?.y}px)`
      } else {
        canvasWrapper.style.transform = `scale(${defaultZoom})`
      }
      canvasWrapper.style.transition = 'transform 0.5s'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultZoom])

  // init pan
  React.useEffect(() => {
    const canvasWrapper = document.getElementById(targetWrapperId)
    let currentX = initialPan?.x ?? 0
    let currentY = initialPan?.y ?? 0
    if (togglePan && canvasWrapper) {
      let allCanvasObj: FabricObject[] | null = null
      if (canvas) {
        allCanvasObj = canvas.getObjects()
        allCanvasObj.forEach((obj) => {
          obj.set({
            selectable: false,
            selected: false,
            isLocked: true,
          })
        })
      }

      const onMouseDown = (e: any) => {
        let lastX = e.clientX
        let lastY = e.clientY
        setGrabbing(true)

        const onMouseMove = (e: any) => {
          const deltaX = e.clientX - lastX
          const deltaY = e.clientY - lastY
          lastX = e.clientX
          lastY = e.clientY

          currentX += deltaX / defaultZoom
          currentY += deltaY / defaultZoom

          canvasWrapper.style.transform = `scale(${defaultZoom}) translate(${currentX}px, ${currentY}px)`
          canvasWrapper.style.transition = 'none'
          onPan?.()
          if (canvas) {
            canvas.selection = false
          }
        }

        const onMouseUp = () => {
          setGrabbing(false)
          if (canvas) {
            canvas.selection = true
          }
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
      }

      canvasWrapper.addEventListener('mousedown', onMouseDown)

      return () => {
        canvasWrapper.removeEventListener('mousedown', onMouseDown)

        allCanvasObj &&
          allCanvasObj.forEach((obj) => {
            obj.set({
              selectable: true,
              isLocked: false,
            })
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, togglePan, defaultZoom])

  return (
    <div
      className={clsx(
        'absolute bottom-2 m-2 p-2 rounded shadow z-10 select-none',
        'certificate-template__footer-pan-zoom',
        togglePan && 'peer canvas-grab',
        isGrabbing && 'peer canvas-grabbing',
        className,
      )}
    >
      <div className="grid gap-2 grid-flow-col items-center">
        <Button
          icon={togglePan ? <Grab /> : <Hand />}
          className={clsx(defaultPanEnabled && '!hidden')}
          onClick={() =>
            setTogglePan((state) => {
              if (!state) {
                canvas?.discardActiveObject()
                notification.info({
                  message: 'Mode geser sedang aktif!',
                  placement: 'bottomRight',
                  key: MSG_INFO_KEY,
                  duration: 0,
                  onClose: () => {
                    setTogglePan(false)
                  },
                })
              } else {
                notification.close(MSG_INFO_KEY)
              }
              return !state
            })
          }
          type={togglePan || defaultPanEnabled ? 'primary' : 'default'}
        />
        <Button
          icon={<ZoomOut className="h-5 w-5 mt-[3px]" />}
          className="!rounded-full"
          type="ghost"
          onClick={() => {
            setDefaultZoom((value) => {
              const target = value - SCALE_STEP
              return Math.max(target, MIN_SCALE)
            })
          }}
        />
        <div>{Math.round(defaultZoom * 10) * 10}%</div>
        <Button
          icon={<ZoomIn className="h-5 w-5 mt-[3px]" />}
          className="!rounded-full"
          type="ghost"
          onClick={() => {
            setDefaultZoom((value) => {
              const target = value + SCALE_STEP
              return Math.min(target, MAX_SCALE)
            })
          }}
        />
      </div>
    </div>
  )
}
